import { useRef, type ElementType, type ReactNode } from 'react';
import { cn } from '../lib/classnames.js';
import { useReducedMotion } from '../lib/useReducedMotion.js';

/**
 * Glass — an elite frosted-glass surface (see `.glass` in globals.css). Deep
 * blur, a top light-catch, a diagonal sheen, a hairline gold rim, and layered
 * depth shadow. When `interactive`, it lifts a touch on hover and a soft specular
 * highlight tracks the pointer — restrained, not bouncy. `featured` strengthens
 * the gold rim for the headline card. Falls back to a static surface under
 * reduced-motion (no lift, no pointer tracking).
 */
export function Glass({
  as,
  className,
  children,
  featured = false,
  interactive = true,
}: {
  as?: ElementType;
  className?: string;
  children?: ReactNode;
  featured?: boolean;
  interactive?: boolean;
}) {
  const Comp = as ?? 'div';
  const ref = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const live = interactive && !reduced;

  const onMove = (e: React.MouseEvent): void => {
    if (!live) return;
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - r.left}px`);
    el.style.setProperty('--my', `${e.clientY - r.top}px`);
  };

  return (
    <Comp
      ref={ref}
      onMouseMove={onMove}
      className={cn('glass', featured && 'glass--featured', live && 'glass--interactive', className)}
    >
      {children}
    </Comp>
  );
}
